<template>
  <el-form
    size="small"
    label-position="top"
    class="monitoring-filters"
  >
    <div
      v-if="loading"
      class="monitoring-filters__progress-bar"
    />
    <el-row
      :gutter="20"
      type="flex"
      class="monitoring-filters__row"
    >
      <el-col :span="hideReparto ? 14 : 8">
        <el-form-item :label="$t('monitoringDashboard.filters.sede')">
          <el-select
            ref="sedeSelect"
            v-model="sedeSelectValue"
            class="width-100"
            filterable
            remote
            clearable
            :remote-method="searchSede"
            :loading="sedeLoading"
            :placeholder="$t('monitoringDashboard.filters.sedePlaceholder')"
            @change="onSedeSelect"
          >
            <el-option
              v-for="option in availableSedeOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-form-item>
        <FilterTags
          :filter="sedeFilterMeta"
          :filter-data="sedeTags"
          @tagClosed="removeSedeTag"
        />
      </el-col>

      <el-col
        v-if="!hideReparto"
        :span="6"
      >
        <el-form-item :label="$t('monitoringDashboard.filters.reparto')">
          <el-select
            ref="repartoSelect"
            v-model="repartoSelectValue"
            class="width-100"
            clearable
            :placeholder="$t('monitoringDashboard.filters.repartoPlaceholder')"
            @change="onRepartoSelect"
          >
            <el-option
              v-for="lab in availableRepartoOptions"
              :key="lab"
              :label="$t(`enums.laboratories.${lab}`)"
              :value="lab"
            />
          </el-select>
        </el-form-item>
        <FilterTags
          :filter="repartoFilterMeta"
          :filter-data="repartoTags"
          @tagClosed="removeRepartoTag"
        />
      </el-col>

      <el-col :span="10">
        <el-form-item label=" ">
          <div class="monitoring-filters__buttons">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-refresh"
              :loading="loading"
              @click="$emit('refresh')"
            >
              {{ $t('monitoringDashboard.refreshAll') }}
            </el-button>
            <el-button
              type="danger"
              size="small"
              @click="$emit('clear')"
            >
              {{ $t('monitoringDashboard.clearFilters') }}
            </el-button>
            <el-button
              v-if="canExport"
              plain
              size="small"
              icon="el-icon-download"
              @click="$emit('export')"
            >
              {{ $t('monitoringDashboard.export') }}
            </el-button>
          </div>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
  import { locationsManagementApi } from '@/api';
  import { laboratories } from '@/config/constants';
  import FilterTags from '@/components/FilterTags.vue';

  export default {
    name: 'MonitoringFilters',
    components: { FilterTags },
    props: {
      // { sede: [{ value, label }, ...], reparto: ['CHIM', ...] }
      value: {
        type: Object,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      hideReparto: {
        type: Boolean,
        default: false,
      },
      canExport: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        // Selected entries must always be present among the options,
        // otherwise el-select renders their tags as bare ids.
        sedeOptions: [...(this.value.sede || [])],
        sedeLoading: false,
        repartoOptions: Object.values(laboratories),
        sedeSelectValue: null,
        repartoSelectValue: null,
      };
    },
    computed: {
      sedeFilterMeta() {
        return { name: 'sede', type: 'select' };
      },
      repartoFilterMeta() {
        return { name: 'reparto', type: 'select' };
      },
      sedeTags() {
        return (this.value.sede || []).map((s) => ({
          key: s.value,
          label: s.label,
        }));
      },
      repartoTags() {
        return (this.value.reparto || []).map((lab) => ({
          key: lab,
          label: this.$t(`enums.laboratories.${lab}`),
        }));
      },
      availableSedeOptions() {
        const selected = new Set((this.value.sede || []).map((s) => s.value));
        return this.sedeOptions.filter((o) => !selected.has(o.value));
      },
      availableRepartoOptions() {
        const selected = new Set(this.value.reparto || []);
        return this.repartoOptions.filter((lab) => !selected.has(lab));
      },
    },
    watch: {
      // Keep the options in sync when the parent resets/hydrates the filter
      // (e.g. clearing the filters sets sede back to []).
      'value.sede': {
        handler(sede) {
          this.mergeSedeOptions(sede || []);
        },
      },
    },
    methods: {
      mergeSedeOptions(selected) {
        const known = new Set(this.sedeOptions.map((o) => o.value));
        this.sedeOptions.push(...selected.filter((s) => !known.has(s.value)));
      },
      async searchSede(query) {
        if (!query || query.length < 3) {
          return;
        }
        this.sedeLoading = true;
        try {
          const response = await locationsManagementApi.autocompleteFetch(
            'name',
            query,
            false,
            true
          );
          this.sedeOptions = [...(this.value.sede || [])];
          this.mergeSedeOptions(
            response.data.map((l) => ({ value: l.id, label: l.name }))
          );
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        } finally {
          this.sedeLoading = false;
        }
      },
      onSedeSelect(id) {
        const option = this.sedeOptions.find((o) => o.value === id);
        if (!option) return;
        this.$emit('input', {
          ...this.value,
          sede: [...(this.value.sede || []), option],
        });
        this.sedeSelectValue = null;
        setTimeout(() => this.$refs.sedeSelect.blur());
      },
      removeSedeTag({ item }) {
        this.$emit('input', {
          ...this.value,
          sede: (this.value.sede || []).filter((s) => s.value !== item.key),
        });
      },
      onRepartoSelect(lab) {
        if (!lab) return;
        this.$emit('input', {
          ...this.value,
          reparto: [...(this.value.reparto || []), lab],
        });
        this.repartoSelectValue = null;
        setTimeout(() => this.$refs.repartoSelect.blur());
      },
      removeRepartoTag({ item }) {
        this.$emit('input', {
          ...this.value,
          reparto: (this.value.reparto || []).filter((lab) => lab !== item.key),
        });
      },
    },
  };
</script>

<style lang="scss">
  .monitoring-filters {
    margin-bottom: 16px;
    padding-bottom: 20px;
    min-height: $filter-form-min-height;
    position: relative;

    &__row {
      flex-wrap: wrap;
    }

    &__buttons {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__progress-bar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(
        90deg,
        $--color-primary 0%,
        $--color-primary 50%,
        transparent 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: monitoringProgressBar 1.5s linear infinite;
      z-index: 9999;
    }

    .el-form-item {
      &__label {
        color: $--color-text-regular;
        min-height: $filter-label-min-height;
      }

      &--small {
        .el-form-item {
          &__label {
            padding-bottom: 15px;
            line-height: 1;
            min-height: $filter-label-min-height;
          }
        }
      }

      .el-autocomplete,
      .el-select {
        width: 100%;
      }
    }

    .el-select .el-select__input {
      color: #fff;
    }
  }

  @keyframes monitoringProgressBar {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: 200% 0%;
    }
  }
</style>
